import React, { useState, useRef, useEffect, useCallback } from "react";
import { Send } from "lucide-react";
import { notify } from "../utils/notify";
import {
  PlusIcon,
  ChatBubbleLeftRightIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { trpc, handleTrpcError } from "@/lib/api/trpc";
import { AnswerScope, ChatMessage } from "@/types/chat.types";
import EmptyState from "@/components/chat/EmptyState";
import ChatMessageComponent from "@/components/chat/ChatMessageComponent";
import LoadingIndicator from "@/components/chat/LoadingIndicator";
import ScopePicker from "@/components/chat/ScopePicker";
import { formatDistanceToNow } from "date-fns";

type ConversationListItem = {
  id: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  source: string;
};

const ChatPage: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [conversationId, setConversationId] = useState<string | undefined>(
    undefined,
  );
  // Retrieval scope (#160): empty = org-wide. Sent explicitly with every turn
  // (ids, or null for org-wide), so the server's stored conversation scope
  // always mirrors what this picker shows.
  const [scopeCollectionIds, setScopeCollectionIds] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const utils = trpc.useContext();

  // Fetch conversation list
  // @ts-ignore - tRPC type inference through `as any` router cast
  const { data: conversationsData, refetch: refetchConversations } =
    trpc.chat.listConversations.useQuery({ limit: 20 });
  const conversations = (conversationsData as ConversationListItem[]) || [];

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Auto-resize textarea as user types
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [input]);

  const chatMutation = trpc.chat.query.useMutation({
    onSuccess: (response) => {
      const assistantMessage: ChatMessage = {
        id: response.messageId,
        role: "assistant",
        content: response.answer,
        citations: response.citations,
        confidence: response.confidence,
        degraded: response.degraded,
        unverifiedSources: response.unverifiedSources,
        suggestedNextSteps: response.suggestedNextSteps,
        scope: response.scope,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setConversationId(response.conversationId);
      // Refresh conversation list so the new/updated conversation appears
      refetchConversations();
    },
    onError: (error) => {
      const { message } = handleTrpcError(error);
      notify.error(message || "Failed to get response");
    },
  });

  const handleSubmit = async (queryText: string) => {
    if (!queryText.trim() || chatMutation.isLoading) return;

    const userMessage: ChatMessage = {
      id: `temp-${Date.now()}`,
      role: "user",
      content: queryText,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    chatMutation.mutate({
      query: queryText,
      conversationId,
      source: "ADMIN_CHAT",
      // Always explicit (#160): ids scope the turn and persist on the
      // conversation; null clears a previously stored scope to org-wide.
      collectionIds: scopeCollectionIds.length > 0 ? scopeCollectionIds : null,
    });
  };

  const handleNewConversation = useCallback(() => {
    setMessages([]);
    setConversationId(undefined);
    setInput("");
    textareaRef.current?.focus();
  }, []);

  const handleLoadConversation = useCallback(
    async (convId: string) => {
      try {
        // @ts-ignore - tRPC type inference through `as any` router cast
        const data = await utils.client.chat.getConversation.query({
          conversationId: convId,
        });
        if (data && Array.isArray(data)) {
          const loaded = data.map(
            (msg: {
              id: string;
              role: string;
              content: string;
              citations: unknown;
              createdAt: string;
            }) => ({
              id: msg.id,
              role: msg.role === "USER" ? "user" : "assistant",
              content: msg.content,
              citations:
                msg.citations &&
                typeof msg.citations === "object" &&
                "citations" in (msg.citations as Record<string, unknown>)
                  ? (msg.citations as Record<string, unknown>).citations
                  : undefined,
              confidence:
                msg.citations &&
                typeof msg.citations === "object" &&
                "confidence" in (msg.citations as Record<string, unknown>)
                  ? (msg.citations as Record<string, unknown>).confidence
                  : undefined,
              suggestedNextSteps:
                msg.citations &&
                typeof msg.citations === "object" &&
                "nextSteps" in (msg.citations as Record<string, unknown>)
                  ? (msg.citations as Record<string, unknown>).nextSteps
                  : undefined,
              scope:
                msg.citations &&
                typeof msg.citations === "object" &&
                "scope" in (msg.citations as Record<string, unknown>)
                  ? ((msg.citations as Record<string, unknown>)
                      .scope as AnswerScope)
                  : undefined,
              timestamp: new Date(msg.createdAt),
            }),
          ) as ChatMessage[];
          setMessages(loaded);
          setConversationId(convId);
          // Restore the picker from the last turn's snapshotted scope (#160)
          // so the next question is sent under the scope this conversation
          // was last using. Scope is only snapshotted on scoped turns, so no
          // snapshot in the final user/assistant pair means org-wide.
          const lastScope = loaded.slice(-2).find((m) => m.scope)?.scope;
          setScopeCollectionIds(
            lastScope ? lastScope.collections.map((c) => c.id) : [],
          );
        }
      } catch (err) {
        notify.error("Failed to load conversation");
        console.error(err);
      }
    },
    [utils],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(input);
    }
  };

  const handleExampleClick = (query: string) => {
    setInput(query);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Conversation history sidebar */}
      {sidebarOpen && (
        <div className="w-56 flex-shrink-0 border-r border-subtle flex flex-col hidden lg:flex">
          <div className="p-3 border-b border-subtle">
            <button
              onClick={handleNewConversation}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-accent rounded-lg hover:bg-accent-strong transition-colors"
            >
              <PlusIcon className="h-4 w-4" />
              New Conversation
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-2">
            <div className="space-y-1">
              {conversations.map((conv) => (
                <button
                  key={conv.id}
                  onClick={() => handleLoadConversation(conv.id)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    conversationId === conv.id
                      ? "bg-accent-tint text-accent"
                      : "text-text-muted hover:bg-surface-2"
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="truncate block">
                      {conv.title
                        ? conv.title.length > 40
                          ? conv.title.slice(0, 40) + "…"
                          : conv.title
                        : "Untitled"}
                    </span>
                    {conv.source === "TEAMS_BOT" && (
                      <span className="flex-shrink-0 inline-flex items-center px-1 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                        Teams
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-text-faint">
                    {formatDistanceToNow(new Date(conv.updatedAt), {
                      addSuffix: true,
                    })}
                  </span>
                </button>
              ))}
              {conversations.length === 0 && (
                <p className="text-xs text-text-faint px-3 py-2">
                  No conversations yet
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Page Header */}
        <div className="flex-shrink-0 mb-6 px-4 pt-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:flex items-center justify-center p-1.5 text-text-faint hover:text-text-muted rounded-lg hover:bg-surface-2 transition-colors"
            >
              {sidebarOpen ? (
                <XMarkIcon className="h-5 w-5" />
              ) : (
                <Bars3Icon className="h-5 w-5" />
              )}
            </button>
            <div>
              <h1 className="font-serif text-3xl font-normal tracking-[-0.005em] text-[var(--text)]">
                Document Chat
              </h1>
              <p className="text-text-faint mt-1 text-sm">
                Ask questions about your documents and get answers with
                citations
              </p>
            </div>
          </div>
          <button
            onClick={handleNewConversation}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-text-muted bg-surface border border-subtle rounded-lg hover:bg-surface-2 transition-colors"
          >
            <ChatBubbleLeftRightIcon className="h-4 w-4" />
            New
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto mb-4 min-h-0 px-4">
          {messages.length === 0 ? (
            <EmptyState onExampleClick={handleExampleClick} />
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <ChatMessageComponent key={message.id} message={message} />
              ))}
              {chatMutation.isLoading && <LoadingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 border-t border-subtle pt-4 px-4 pb-4">
          <div className="mb-2">
            <ScopePicker
              selectedIds={scopeCollectionIds}
              onChange={setScopeCollectionIds}
              disabled={chatMutation.isLoading}
            />
          </div>
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask a question... (Shift+Enter for new line)"
                className="w-full px-4 py-3 pr-12 text-sm bg-surface border border-subtle rounded-lg focus:border-accent focus:ring-1 focus:ring-accent focus:outline-none text-text-base resize-none min-h-[48px] max-h-[200px]"
                rows={1}
                disabled={chatMutation.isLoading}
              />
            </div>
            <button
              onClick={() => handleSubmit(input)}
              disabled={!input.trim() || chatMutation.isLoading}
              className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-accent text-white rounded-lg hover:bg-accent-strong disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Send message"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-xs text-text-faint mt-2">
            Press Enter to send, Shift+Enter for new line
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChatPage;
